/**
 * MoEngageQueue — production-grade analytics event queue for the MoEngage Web SDK.
 *
 * Problem this solves
 * -------------------
 * The MoEngage SDK loads asynchronously.  Events fired before the SDK is ready
 * would ordinarily be silently dropped.  This queue buffers those events,
 * persists them across page refreshes, and flushes them the moment the SDK
 * signals readiness — with automatic retry and priority-based ordering.
 *
 * Architecture overview
 * ---------------------
 *
 *   caller ──► track()
 *                │
 *                ├── SDK ready + online? ──► _sendEvent() ──► moEngageService.trackEvent()
 *                │
 *                └── not ready / offline? ──► _enqueue()
 *                                                  │
 *                                    [localStorage persistence]
 *                                                  │
 *                             SDK becomes ready ──► flushQueue()
 *                                                  │
 *                                          _processBatch()
 *                                                  │
 *                                     fail? ──► shouldRetry()
 *                                                  │
 *                                     yes  ──► incrementRetry() + setTimeout
 *                                     no   ──► drop event
 *
 * Key guarantees
 * --------------
 * • FIFO within the same priority tier.
 * • HIGH priority events always flush before NORMAL, which flush before LOW.
 * • No duplicate flush (isFlushing guard).
 * • Crash-recovery: "processing" events restored as "pending" on next page load.
 * • Queue cap with graceful eviction (oldest LOW-priority event dropped first).
 * • Online/offline awareness — flush deferred while offline.
 * • Middleware pipeline for event transformation / filtering.
 * • Fully observable via getStats() for dashboards / debugging.
 */

import {
	BATCH_SIZE,
	EVENT_PRIORITY,
	FLUSH_INTERVAL_MS,
	MAX_QUEUE_SIZE,
	MOENGAGE_READY_EVENT,
	POLLING_INTERVAL_MS,
	POLLING_MAX_ATTEMPTS,
	QUEUE_STATUS,
} from "./constants";
import { calculateRetryDelay, incrementRetry, shouldRetry } from "./retry";
import {
	clearQueueStorage,
	loadQueueFromStorage,
	saveQueueToStorage,
} from "./storage";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Generate a collision-resistant event ID without a crypto dependency. */
function generateId() {
	return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Debug-only logger — compiled away in production if tree-shaken. */
function debug(...args) {
	if (process.env.NODE_ENV !== "production") {
		// eslint-disable-next-line no-console
		console.debug("[MoEngageQueue]", ...args);
	}
}

// ---------------------------------------------------------------------------
// Core class
// ---------------------------------------------------------------------------

class MoEngageQueue {
	constructor() {
		// ── In-memory queue ──────────────────────────────────────────────────
		/** @type {Array<QueuedEvent>} */
		this._queue = [];

		// ── Concurrency guard ────────────────────────────────────────────────
		/** Prevent two flush calls from running simultaneously. */
		this._isFlushing = false;

		// ── SDK readiness detection ──────────────────────────────────────────
		/** setInterval handle for the readiness poller. */
		this._pollingTimer = null;
		/** Number of polling cycles elapsed. */
		this._pollingAttempts = 0;
		/** setInterval handle for the periodic post-ready flush. */
		this._flushTimer = null;
		/** True once we have received the SDK-ready signal. */
		this._sdkReady = false;

		// ── Middleware pipeline ───────────────────────────────────────────────
		/**
		 * Each middleware is a function: (event: QueuedEvent) => QueuedEvent | null.
		 * Returning null drops the event.
		 * @type {Array<Function>}
		 */
		this._middlewares = [];

		// ── Service references (injected via init) ───────────────────────────
		/**
		 * Lazily set by init() to avoid import-time circular dependencies.
		 * @type {{ isReady: () => boolean, trackEvent: Function } | null}
		 */
		this._service = null;

		// ── Initialisation flag ──────────────────────────────────────────────
		this._initialized = false;
	}

	// =========================================================================
	// Public initialisation
	// =========================================================================

	/**
	 * Wire up the queue to a concrete MoEngage service implementation.
	 *
	 * Call this once, typically inside analyticsManager.initialize(), right
	 * after the MoEngage service itself has been imported.
	 *
	 * @param {{ isReady: () => boolean, trackEvent: Function }} moEngageService
	 */
	init(moEngageService) {
		if (this._initialized) {
			return;
		}

		this._service = moEngageService;
		this._initialized = true;

		// Restore events that were queued before the previous page unload.
		this._restoreFromStorage();

		// Start watching for SDK readiness.
		this._startReadinessDetection();

		// Defer flush when we come back online.
		this._bindNetworkListeners();

		debug("Queue initialised.");
	}

	// =========================================================================
	// Public API
	// =========================================================================

	/**
	 * Track an event.
	 *
	 * If the MoEngage SDK is already initialised and the device is online, the
	 * event is dispatched immediately.  Otherwise it is placed in the queue and
	 * will be flushed automatically once the SDK becomes ready.
	 *
	 * @param {string} eventName
	 * @param {Object} [properties={}]  - Arbitrary key/value event properties.
	 * @param {Object} [options={}]     - Optional SDK-level options.
	 * @param {number} [priority]       - EVENT_PRIORITY.HIGH | NORMAL | LOW.
	 */
	track(
		eventName,
		properties = {},
		options = {},
		priority = EVENT_PRIORITY.NORMAL
	) {
		if (!eventName || typeof eventName !== "string") {
			console.warn(
				"[MoEngageQueue] track() requires a non-empty string eventName."
			);
			return;
		}

		/** @type {QueuedEvent} */
		const rawEvent = {
			id: generateId(),
			eventName,
			properties,
			options,
			priority,
			enqueuedAt: new Date().toISOString(),
			retryCount: 0,
			status: QUEUE_STATUS.PENDING,
		};

		// Run through the middleware pipeline — any middleware may transform or drop.
		const event = this._applyMiddlewares(rawEvent);
		if (event === null) {
			debug("Event dropped by middleware:", eventName);
			return;
		}

		if (this.isMoEngageReady() && navigator.onLine) {
			// Fast path: SDK is live, skip the queue entirely.
			this._sendEvent(event).catch(() => {
				// Failure on the fast path: give the event a second chance via queue.
				this._enqueue(incrementRetry(event));
			});
		} else {
			this._enqueue(event);
		}
	}

	/**
	 * Manually trigger a flush of all pending events.
	 *
	 * @returns {Promise<void>} Resolves when the current flush batch completes.
	 *                          Does NOT wait for scheduled retries.
	 */
	async flushQueue() {
		if (this._isFlushing) {
			debug("Flush already in progress — skipping.");
			return;
		}
		if (!this.isMoEngageReady()) {
			debug("SDK not ready — flush deferred.");
			return;
		}

		const pending = this._queue.filter(
			(e) => e.status === QUEUE_STATUS.PENDING
		);
		if (pending.length === 0) {
			return;
		}

		this._isFlushing = true;
		debug(`Flushing ${pending.length} event(s) in batches of ${BATCH_SIZE}.`);

		try {
			const batches = Array.from(
				{ length: Math.ceil(pending.length / BATCH_SIZE) },
				(_, i) => pending.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE)
			);
			await batches.reduce(
				(chain, batch) => chain.then(() => this._processBatch(batch)),
				Promise.resolve()
			);
		} finally {
			this._isFlushing = false;
			// Always sync storage after a flush attempt, even on partial failure.
			saveQueueToStorage(this._queue);
		}
	}

	/**
	 * Whether the MoEngage SDK is ready to receive events.
	 *
	 * @returns {boolean}
	 */
	isMoEngageReady() {
		if (!this._service) {
			return false;
		}
		try {
			return this._service.isReady();
		} catch {
			return false;
		}
	}

	/**
	 * Register a middleware function.
	 *
	 * Middlewares are called in registration order.  Returning null from any
	 * middleware drops the event; returning the (possibly mutated) event object
	 * passes it to the next middleware.
	 *
	 * Example — add a session ID to every event:
	 *   moEngageQueue.use(event => ({
	 *     ...event,
	 *     properties: { ...event.properties, session_id: getSessionId() }
	 *   }));
	 *
	 * Example — drop internal test events in production:
	 *   moEngageQueue.use(event =>
	 *     event.eventName.startsWith('__test__') ? null : event
	 *   );
	 *
	 * @param {Function} fn - (event: QueuedEvent) => QueuedEvent | null
	 * @returns {MoEngageQueue} `this` for chaining.
	 */
	use(fn) {
		if (typeof fn !== "function") {
			console.warn("[MoEngageQueue] use() expects a function.");
			return this;
		}
		this._middlewares.push(fn);
		return this;
	}

	/**
	 * Return a snapshot of the current queue (defensive copy).
	 *
	 * @returns {Array<QueuedEvent>}
	 */
	getQueue() {
		return [...this._queue];
	}

	/**
	 * Return real-time queue statistics — useful for observability dashboards.
	 *
	 * @returns {{
	 *   total: number,
	 *   pending: number,
	 *   processing: number,
	 *   failed: number,
	 *   isFlushing: boolean,
	 *   isSdkReady: boolean,
	 *   isOnline: boolean,
	 * }}
	 */
	getStats() {
		return {
			total: this._queue.length,
			pending: this._queue.filter((e) => e.status === QUEUE_STATUS.PENDING)
				.length,
			processing: this._queue.filter(
				(e) => e.status === QUEUE_STATUS.PROCESSING
			).length,
			failed: this._queue.filter((e) => e.status === QUEUE_STATUS.FAILED)
				.length,
			isFlushing: this._isFlushing,
			isSdkReady: this.isMoEngageReady(),
			isOnline: navigator.onLine,
		};
	}

	/**
	 * Discard all pending events from memory and storage.
	 * Use with care — discarded events cannot be recovered.
	 */
	clearQueue() {
		this._queue = [];
		clearQueueStorage();
		debug("Queue cleared.");
	}

	/**
	 * Tear down timers and event listeners.
	 * Call this before unmounting the app or in tests.
	 */
	destroy() {
		this._stopPolling();
		if (this._flushTimer !== null) {
			clearInterval(this._flushTimer);
			this._flushTimer = null;
		}
		window.removeEventListener(MOENGAGE_READY_EVENT, this._onSdkReadyBound, {
			once: true,
		});
		debug("Queue destroyed.");
	}

	// =========================================================================
	// Storage restoration
	// =========================================================================

	_restoreFromStorage() {
		const stored = loadQueueFromStorage();

		// Events that were mid-flight when the page closed are reset to PENDING
		// so they are retried on the next session.
		this._queue = stored.map((event) => ({
			...event,
			status:
				event.status === QUEUE_STATUS.PROCESSING
					? QUEUE_STATUS.PENDING
					: event.status,
		}));

		if (this._queue.length > 0) {
			debug(`Restored ${this._queue.length} event(s) from localStorage.`);
		}
	}

	// =========================================================================
	// SDK readiness detection — dual strategy
	// =========================================================================

	_startReadinessDetection() {
		// Strategy 1 — event-based (zero-latency if SDK fires the ready event).
		this._onSdkReadyBound = this._onSdkReady.bind(this);
		window.addEventListener(MOENGAGE_READY_EVENT, this._onSdkReadyBound, {
			once: true,
		});

		// Strategy 2 — polling (fallback for SDKs that don't emit a ready event).
		this._pollingTimer = setInterval(() => {
			this._pollingAttempts += 1;

			if (this.isMoEngageReady()) {
				this._stopPolling();
				this._onSdkReady();
				return;
			}

			if (this._pollingAttempts >= POLLING_MAX_ATTEMPTS) {
				console.warn(
					`[MoEngageQueue] SDK not ready after ${POLLING_MAX_ATTEMPTS} polls ` +
						`(${(POLLING_MAX_ATTEMPTS * POLLING_INTERVAL_MS) / 1000}s). ` +
						"Stopping poller — events remain queued."
				);
				this._stopPolling();
			}
		}, POLLING_INTERVAL_MS);
	}

	_stopPolling() {
		if (this._pollingTimer !== null) {
			clearInterval(this._pollingTimer);
			this._pollingTimer = null;
		}
	}

	_onSdkReady() {
		if (this._sdkReady) {
			return;
		} // idempotent
		this._sdkReady = true;

		debug("SDK is ready — starting initial flush.");
		this._stopPolling();
		this.flushQueue();

		// Periodic flush picks up any events that arrive after the initial flush.
		if (this._flushTimer === null) {
			this._flushTimer = setInterval(() => {
				const hasPending = this._queue.some(
					(e) => e.status === QUEUE_STATUS.PENDING
				);
				if (hasPending) {
					this.flushQueue();
				}
			}, FLUSH_INTERVAL_MS);
		}
	}

	// =========================================================================
	// Network awareness
	// =========================================================================

	_bindNetworkListeners() {
		window.addEventListener("online", () => {
			debug("Network restored — flushing queued events.");
			this.flushQueue();
		});

		window.addEventListener("offline", () => {
			debug("Network offline — events will be queued until reconnected.");
		});
	}

	// =========================================================================
	// Middleware pipeline
	// =========================================================================

	_applyMiddlewares(event) {
		let current = event;
		for (const mw of this._middlewares) {
			try {
				current = mw(current);
				if (current === null || current === undefined) {
					return null;
				}
			} catch (err) {
				console.error("[MoEngageQueue] Middleware threw an error:", err);
				// A crashing middleware drops the event rather than crashing the app.
				return null;
			}
		}
		return current;
	}

	// =========================================================================
	// Queue management
	// =========================================================================

	/**
	 * Insert an event into the in-memory queue.
	 *
	 * When the queue is full, the oldest LOW-priority event is evicted first.
	 * If no LOW-priority event exists, the globally oldest event is evicted.
	 * This ensures HIGH-priority events are never silently dropped just because
	 * the queue filled up with low-value events.
	 */
	_enqueue(event) {
		if (this._queue.length >= MAX_QUEUE_SIZE) {
			const lowIdx = this._queue.findIndex(
				(e) => e.priority === EVENT_PRIORITY.LOW
			);
			if (lowIdx !== -1) {
				const dropped = this._queue.splice(lowIdx, 1)[0];
				console.warn(
					`[MoEngageQueue] Queue full — evicted LOW priority event: "${dropped.eventName}".`
				);
			} else {
				const dropped = this._queue.shift();
				console.warn(
					`[MoEngageQueue] Queue full — evicted oldest event: "${dropped.eventName}".`
				);
			}
		}

		this._queue.push(event);
		this._sortQueue();
		saveQueueToStorage(this._queue);

		debug(
			`Queued "${event.eventName}" (priority=${event.priority}, ` +
				`queueSize=${this._queue.length}).`
		);
	}

	/**
	 * Sort the queue so HIGH-priority events flush first.
	 * Within the same priority, preserve FIFO order by enqueuedAt.
	 */
	_sortQueue() {
		this._queue.sort((a, b) => {
			const pDiff =
				(a.priority ?? EVENT_PRIORITY.NORMAL) -
				(b.priority ?? EVENT_PRIORITY.NORMAL);
			if (pDiff !== 0) {
				return pDiff;
			}
			return (
				new Date(a.enqueuedAt).getTime() - new Date(b.enqueuedAt).getTime()
			);
		});
	}

	// =========================================================================
	// Event dispatch
	// =========================================================================

	/**
	 * Dispatch a single event via the injected MoEngage service.
	 *
	 * @param {QueuedEvent} event
	 * @returns {Promise<void>}
	 * @throws Rethrows SDK errors so callers can handle retry logic.
	 */
	async _sendEvent(event) {
		await this._service.trackEvent(
			event.eventName,
			event.properties,
			event.options
		);
		debug(`Sent "${event.eventName}".`);
	}

	// =========================================================================
	// Batch processing
	// =========================================================================

	/**
	 * Process an array of events concurrently (within the batch).
	 * Promise.allSettled ensures one failure doesn't abort the whole batch.
	 *
	 * @param {Array<QueuedEvent>} batch
	 */
	async _processBatch(batch) {
		await Promise.allSettled(batch.map((event) => this._processEvent(event)));
	}

	/**
	 * Process a single queued event:
	 * 1. Mark as PROCESSING (crash-recovery marker).
	 * 2. Attempt dispatch.
	 * 3a. Success → remove from queue.
	 * 3b. Failure + retriable → increment retry, schedule delayed retry.
	 * 3c. Failure + max retries → permanently drop.
	 *
	 * @param {QueuedEvent} event
	 */
	async _processEvent(event) {
		// Locate the live queue slot — it may have been removed by a concurrent batch.
		const liveIdx = this._queue.findIndex((e) => e.id === event.id);
		if (liveIdx === -1) {
			return;
		}

		// Guard against re-processing an event that is already in flight.
		if (this._queue[liveIdx].status === QUEUE_STATUS.PROCESSING) {
			return;
		}

		this._queue[liveIdx] = {
			...this._queue[liveIdx],
			status: QUEUE_STATUS.PROCESSING,
		};

		try {
			await this._sendEvent(event);

			// Success — remove from queue.
			this._queue = this._queue.filter((e) => e.id !== event.id);
			debug(`Removed successfully sent event "${event.eventName}".`);
		} catch (err) {
			const current = this._queue.find((e) => e.id === event.id);
			if (!current) {
				return;
			} // Already removed by another path.

			if (shouldRetry(current)) {
				const updated = incrementRetry(current);
				const delay = calculateRetryDelay(updated.retryCount);

				const retryIdx = this._queue.findIndex((e) => e.id === event.id);
				if (retryIdx !== -1) {
					this._queue[retryIdx] = updated;
					saveQueueToStorage(this._queue);
				}

				console.warn(
					`[MoEngageQueue] "${event.eventName}" failed — ` +
						`retry ${updated.retryCount}/${3} in ${delay}ms.`,
					err
				);

				// Schedule an isolated retry outside the current flush.
				setTimeout(() => {
					if (!this.isMoEngageReady()) {
						return;
					}
					const retryEvent = this._queue.find((e) => e.id === event.id);
					if (retryEvent && retryEvent.status === QUEUE_STATUS.PENDING) {
						this._processEvent(retryEvent).catch(() => {});
					}
				}, delay);
			} else {
				// Max retries exhausted — log and purge.
				console.error(
					`[MoEngageQueue] "${event.eventName}" permanently dropped ` +
						`after ${event.retryCount} retries.`,
					err
				);
				this._queue = this._queue.filter((e) => e.id !== event.id);
				saveQueueToStorage(this._queue);
			}
		}
	}
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

/**
 * Application-wide MoEngage queue instance.
 *
 * Import this singleton wherever you need to track events.
 * Call `moEngageQueue.init(moEngageService)` once during app initialisation.
 *
 * @example
 * // App bootstrap
 * import moEngageQueue from './analytics/moengage/MoEngageQueue';
 * import moEngageService from './analytics/services/moEngage';
 * moEngageQueue.init(moEngageService);
 *
 * // Anywhere in the app
 * moEngageQueue.track('page_viewed', { page: 'home' });
 */
const moEngageQueue = new MoEngageQueue();
export default moEngageQueue;
export { MoEngageQueue, EVENT_PRIORITY };

/**
 * @typedef {Object} QueuedEvent
 * @property {string}  id          - Unique event identifier.
 * @property {string}  eventName   - MoEngage event name.
 * @property {Object}  properties  - Event payload.
 * @property {Object}  options     - SDK-level options.
 * @property {number}  priority    - EVENT_PRIORITY value.
 * @property {string}  enqueuedAt  - ISO timestamp when the event was enqueued.
 * @property {number}  retryCount  - How many times dispatch has been attempted.
 * @property {string}  status      - QUEUE_STATUS value.
 * @property {string}  [lastRetryAt]   - ISO timestamp of the last retry attempt.
 * @property {string}  [failedAt]      - ISO timestamp when event was permanently dropped.
 * @property {string}  [failureReason] - Human-readable reason for permanent failure.
 */
